import React, { useState, useEffect, useRef, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "../components/common/toast";
import { getApiErrorMessage, getUploadErrorMsg, getBestErrorMessageFromData } from "../utils/error";
import { productService, companyService } from "../services/api";
import {
  Table,
  EditIcon,
  // DeleteIcon, // Product delete disabled in UI — keep import for when re-enabled
  FormPopup,
  // DeletePopUp,
  UploadPreviewPopUp,
} from "../components/common";
import { parseFileToPreview } from "../utils/uploadPreviewParser";
import { downloadProductUploadTemplate } from "../utils/downloadProductUploadTemplate";
import ProductDetailsModal from "../components/common/ProductDetailsModal";
import SkeletonTable from "../components/SkeletonTable";

// --- Date / Shelf Life helpers (2-of-3 auto-calculation)
function normalizeDateInput(v) {
  if (v === undefined || v === null || v === "") return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(String(v).trim());
  return Number.isNaN(d.getTime()) ? null : d;
}

function addDaysToDate(date, days) {
  const d = date instanceof Date ? date : normalizeDateInput(date);
  if (!d || typeof days !== "number") return null;
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

function diffDays(date1, date2) {
  const d1 = date1 instanceof Date ? date1 : normalizeDateInput(date1);
  const d2 = date2 instanceof Date ? date2 : normalizeDateInput(date2);
  if (!d1 || !d2) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.round((d2.getTime() - d1.getTime()) / msPerDay);
}

function subtractDaysFromDate(date, days) {
  const d = date instanceof Date ? date : normalizeDateInput(date);
  if (!d || typeof days !== "number") return null;
  const out = new Date(d);
  out.setDate(out.getDate() - days);
  return out;
}

function formatDateYMD(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function parseShelfDays(v) {
  if (v === "" || v === undefined || v === null) return null;
  const n = Number(v);
  if (Number.isNaN(n) || n < 0 || !Number.isInteger(n)) return null;
  return n;
}

function getOnboardedCompanyError(dataOrError) {
  const data = dataOrError?.response?.data || dataOrError?.data || dataOrError || {};
  const rowErrors = Array.isArray(data?.errors)
    ? data.errors
    : Array.isArray(data?.data?.errors)
      ? data.data.errors
      : [];
  const firstRowMessage = rowErrors.find((e) => typeof e?.error === "string")?.error || "";
  const topMessage = typeof data?.message === "string" ? data.message : "";
  const message = firstRowMessage || topMessage;
  const match = message.match(/Company '([^']+)' is not onboarded/i);
  if (!match) return null;
  return `Company '${match[1]}' is not onboarded.`;
}

const PRODUCT_COLUMNS = [
  { key: "sku", label: "SKU" },
  { key: "skuName", label: "SKU Name" },
  // { key: "description", label: "Description" },
  // { key: "storageType", label: "Storage Type", align: "center" },
  // { key: "productType", label: "Product Type", align: "center" },
  // { key: "supplierName", label: "Supplier" },
  // {
  //   key: "skuCategory",
  //   label: "SKU Type",
  //   align: "center",
  //   render: (val) => (val ? String(val).replace("_", " ") : "—"),
  // },
  // {
  //   key: "manufacturingDate",
  //   label: "MFG Date",
  //   align: "center",
  //   render: (val) => (val ? new Date(val).toLocaleDateString() : "—"),
  // },
  // {
  //   key: "expiryDate",
  //   label: "EXP Date",
  //   align: "center",
  //   render: (val) => (val ? new Date(val).toLocaleDateString() : "—"),
  // },

  {
    key: "companies",
    label: "Company",
    render: (_, row) => {
      const list = row.companies || [];
      if (!list.length) return "—";
      return list.map((c) => `${c.name}`)
      // (${c.companyCode || '—'})`).join(", ");
    },
  },
  { key: "uom", label: "UOM", align: "center" },
  // {
  //   key: "casesPerPallet",
  //   label: "Pallet",
  //   align: "center",
  //   render: (val) => (val != null && val !== "" ? val : "—"),
  // },
  // { key: "weight", label: "Gross Weight as per Pallet UOM (kg)", align: "center" },
  {
    key: "dimensions",
    label: "Dimensions (L×B×H cm)",
    render: (_, row) =>
      row.dimensions
        ? `${row.dimensions.length} × ${row.dimensions.breadth} × ${row.dimensions.height}`
        : "—",
  },
  {
    key: "isBatchControlled",
    label: "Batch Controlled",
    align: "center",
    render: (val) => (val ? "Yes" : "No"),
  },
];

export function ProductMaster() {
  const [products, setProducts] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;
  const [showForm, setShowForm] = useState(false);
  const [editingProductId, setEditingProductId] = useState(null);
  const [savedQtyInPallet, setSavedQtyInPallet] = useState(undefined);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewError, setViewError] = useState(null);
  const viewRowRef = useRef(null);

  const [formData, setFormData] = useState({
    sku: "",
    skuName: "",
    // description: "",
    storageType: "",
    productType: "",
    uom: "",
    basePrice: 0,
    casesPerPallet: "",
    // innerUom: "",
    // palletUom: "",
    // innerUomQty: "",
    // outerUomQty removed from Product configuration - do not use in active flow
    // outerUomQty: "",
    conversionQtyPerPallet: "",
    qtyInPallet: "",
    weight: 0,
    // Backend-calculated; UI uses for Edit display (no live recalculation)
    weightPerPallet: 0,
    dimensions: { length: 0, breadth: 0, height: 0 },
    isBatchControlled: true,
    companies: [],
    supplierName: "",
    skuCategory: "NON_CORE", // default
    // manufacturingDate, expiryDate, location removed from Product Master - do not use in active flow
    // manufacturingDate: "",
    // expiryDate: "",
    shelfLifeDays: "",
    // location: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [deletePopUpOpen, setDeletePopUpOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [uploadPreviewOpen, setUploadPreviewOpen] = useState(false);
  const [uploadPreviewFileName, setUploadPreviewFileName] = useState("");
  const [uploadPreviewHeaders, setUploadPreviewHeaders] = useState([]);
  const [uploadPreviewRows, setUploadPreviewRows] = useState([]);
  const [uploadInvalidPreviewRows, setUploadInvalidPreviewRows] = useState([]);
  const [uploadSummary, setUploadSummary] = useState({ totalRows: 0, validRows: 0, invalidRows: 0 });
  const [uploadHasCriticalErrors, setUploadHasCriticalErrors] = useState(false);
  const [uploadSelectedFile, setUploadSelectedFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [derivedDateField, setDerivedDateField] = useState(null);

  // OLD LOGIC (kept): company filtering by contract.status was done in fetchCompanies.
  // NEW LOGIC: use onboardStatus for all company-selection flows (create/edit/bulk upload UI).
  const filteredCompanies = useMemo(
    () =>
      (companies || []).filter(
        (c) => (c?.contract?.onboardStatus || "pending") === "completed"
      ),
    [companies]
  );

  // UI behavior:
  // - Inner UOM restricted when Outer UOM is PCS / L / KG (lowest-unit outers)
  // - Pallet UOM: if Inner selected → inner; else → outer (always auto, not user-editable)
  useEffect(() => {
    const outer = String(formData.uom || "").trim().toUpperCase();
    const inner = String(formData.innerUom || "").trim().toUpperCase();
    const isRestrictedOuter = ["PCS", "L", "KG"].includes(outer);

    if (isRestrictedOuter) {
      setFormData((prev) => {
        if (!prev.innerUom && !prev.innerUomQty) return prev;
        return {
          ...prev,
          innerUom: "",
          innerUomQty: "",
        };
      });
    }

    const effectiveInner = isRestrictedOuter ? "" : inner;
    const nextPallet = effectiveInner || outer;
    if (nextPallet) {
      setFormData((prev) => {
        if (String(prev.palletUom || "").trim().toUpperCase() === nextPallet) return prev;
        return { ...prev, palletUom: nextPallet };
      });
    }
  }, [formData.uom, formData.innerUom]);

  useEffect(() => {
    if (formData.conversionQtyPerPallet || formData.innerUomQty) {
      console.log("Recalculating trigger state:", formData);
    }
  }, [formData.conversionQtyPerPallet, formData.innerUomQty, formData]);

  useEffect(() => {
    const conv = Number(formData.conversionQtyPerPallet);
    const innerUomSelected = String(formData.innerUom || "").trim();
    const innerQtyRaw = formData.innerUomQty;
    const inner = Number(innerQtyRaw);

    if (!conv || conv < 1) {
      setFormData((prev) => (prev.qtyInPallet === "" ? prev : { ...prev, qtyInPallet: "" }));
      return;
    }

    if (innerUomSelected) {
      if (
        innerQtyRaw === "" ||
        innerQtyRaw == null ||
        !Number.isFinite(inner) ||
        inner <= 0
      ) {
        setFormData((prev) => (prev.qtyInPallet === "" ? prev : { ...prev, qtyInPallet: "" }));
        return;
      }
    }

    const result = 1;

    console.log("QtyInPallet Calculation:", {
      innerUom: innerUomSelected || undefined,
      innerUomQty: innerUomSelected ? inner : undefined,
      conversionQtyPerPallet: conv,
      result,
    });

    setFormData((prev) => (prev.qtyInPallet === result ? prev : { ...prev, qtyInPallet: result }));
  }, [formData.conversionQtyPerPallet, formData.innerUomQty, formData.innerUom]);

  const uiWeightPerPallet = useMemo(() => {
    // In Edit mode, show backend value (do not live-recalculate on conversion changes).
    if (editingProductId) {
      const v = Number(formData.weightPerPallet || 0);
      return Number.isFinite(v) ? v : 0;
    }
    const grossWeightAsPerPalletUom = Number(formData.weight || 0);
    const conversionQtyPerPallet = Number(formData.conversionQtyPerPallet || 0);
    if (
      !Number.isFinite(grossWeightAsPerPalletUom) ||
      !Number.isFinite(conversionQtyPerPallet) ||
      conversionQtyPerPallet < 1
    ) {
      return 0;
    }
    const p = grossWeightAsPerPalletUom * conversionQtyPerPallet;
    return Number.isFinite(p) ? p : 0;
  }, [editingProductId, formData.weightPerPallet, formData.weight, formData.conversionQtyPerPallet]);

  useEffect(() => {
    if (editingProductId) return;
    const grossWeightAsPerPalletUom = Number(formData.weight || 0);
    const conversionQtyPerPallet = Number(formData.conversionQtyPerPallet || 0);
    let weightPerPallet = 0;
    if (
      Number.isFinite(grossWeightAsPerPalletUom) &&
      Number.isFinite(conversionQtyPerPallet) &&
      conversionQtyPerPallet >= 1
    ) {
      const p = grossWeightAsPerPalletUom * conversionQtyPerPallet;
      weightPerPallet = Number.isFinite(p) ? p : 0;
    }
    console.log("WeightPerPallet UI:", {
      grossWeightAsPerPalletUom,
      conversionQtyPerPallet,
      weightPerPallet,
    });
  }, [editingProductId, formData.weight, formData.conversionQtyPerPallet]);

  const getColumnValue = (row, keys = []) => {
    for (const key of keys) {
      if (row[key] !== undefined && row[key] !== null) return row[key];
    }
    return "";
  };

  const validatePreviewRows = (rows = []) => {
    const invalidIdx = [];
    const rowErrors = [];
    let validRows = 0;

    rows.forEach((row, idx) => {
      const conversionRaw = getColumnValue(row, [
        "conversionQtyPerPallet",
        "Conversion Qty Per Pallet",
        "Conversion Quantity Per Pallet",
        "Conversion Per Pallet",
      ]);
      const innerRaw = getColumnValue(row, ["innerUomQty", "Inner UOM Qty", "Inner Uom Qty", "Inner UOM Quantity"]);
      const innerUomCol = getColumnValue(row, ["innerUom", "Inner UOM", "Inner Uom"]);
      const innerUomStr =
        innerUomCol !== undefined && innerUomCol !== null
          ? String(innerUomCol).trim()
          : "";
      const outerUomCol = getColumnValue(row, ["uom", "Unit of Measure (UOM)", "Outer UOM"]);
      const outerUomStr = String(outerUomCol ?? "").trim().toUpperCase();
      const rowNumber = idx + 2;
      let hasCriticalError = false;

      const convNum = Number(String(conversionRaw ?? "").replace(/,/g, "").trim());
      if (conversionRaw === "" || conversionRaw === null || conversionRaw === undefined || Number.isNaN(convNum) || convNum < 1) {
        invalidIdx.push(idx);
        rowErrors.push(`Row ${rowNumber}: Conversion Qty per Pallet missing or invalid`);
        hasCriticalError = true;
      }

      if (["PCS", "KG", "L"].includes(outerUomStr) && innerUomStr) {
        if (!invalidIdx.includes(idx)) invalidIdx.push(idx);
        rowErrors.push(`Row ${rowNumber}: Inner UOM is not allowed when Outer UOM is PCS, KG, or L`);
        hasCriticalError = true;
      }

      if (innerUomStr) {
        if (innerRaw === "" || innerRaw === null || innerRaw === undefined) {
          if (!invalidIdx.includes(idx)) invalidIdx.push(idx);
          rowErrors.push(`Row ${rowNumber}: Inner UOM Qty is required when Inner UOM is selected`);
          hasCriticalError = true;
        } else {
          const innerNum = Number(String(innerRaw).replace(/,/g, "").trim());
          if (Number.isNaN(innerNum) || innerNum <= 0) {
            if (!invalidIdx.includes(idx)) invalidIdx.push(idx);
            rowErrors.push(`Row ${rowNumber}: Inner UOM Qty is required when Inner UOM is selected`);
            hasCriticalError = true;
          }
        }
      }

      if (!innerUomStr && innerRaw !== "" && innerRaw !== null && innerRaw !== undefined) {
        const innerNum = Number(String(innerRaw).replace(/,/g, "").trim());
        if (Number.isNaN(innerNum) || innerNum < 0) {
          if (!invalidIdx.includes(idx)) invalidIdx.push(idx);
          rowErrors.push(`Row ${rowNumber}: Inner UOM Qty invalid`);
          hasCriticalError = true;
        }
      }

      if (!hasCriticalError) validRows += 1;
    });

    return {
      invalidIdx,
      rowErrors,
      summary: {
        totalRows: rows.length,
        validRows,
        invalidRows: rows.length - validRows,
      },
      hasCriticalErrors: rowErrors.length > 0,
    };
  };

  const isRowEmpty = (row) => {
    return !row["SKU Name"] && !row["Unit of Measure (UOM)"];
  };

  // useEffect(() => {
  //   fetchProducts();
  //   fetchCompanies();
  // }, []);


  useEffect(() => {
   fetchCompanies();
 }, []);
 
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setPage(1);
    }, 450);
    return () => clearTimeout(t);
  }, [searchTerm]);


  const fetchCompanies = async () => {
    try {
      const result = await companyService.getCompanies({ isActive: "true" });
      const list = result.data || [];
      // OLD LOGIC (kept for reference):
      // setCompanies(
      //   Array.isArray(list)
      //     ? list.filter((c) => String(c?.contract?.status || "").toLowerCase() === "completed")
      //     : []
      // );
      setCompanies(
        Array.isArray(list) ? list : []
      );
    } catch (error) {
      console.error("Error fetching companies:", error);
    }
  };

  // useEffect(() => {
  //   const t = setTimeout(() => fetchProducts(), 300);
  //   return () => clearTimeout(t);
  // }, [searchTerm]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const {
    data: productsResponse,
    isLoading,
    isFetching,
    refetch: refetchProducts,
  } = useQuery({
    queryKey: ["products", page, limit, debouncedSearchTerm],
    queryFn: async () => {
      const params = { page, limit };
      if (debouncedSearchTerm && debouncedSearchTerm.trim()) {
        params.search = debouncedSearchTerm.trim();
      }
      return productService.getProducts(params);
    },
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  useEffect(() => {
    if (!productsResponse) return;
    const incoming = Array.isArray(productsResponse?.data)
      ? productsResponse.data
      : [];
    const nextTotalPages = Math.max(1, Number(productsResponse?.totalPages || 1));
    setTotalPages(nextTotalPages);
    setProducts(incoming);
  }, [productsResponse, page]);

  const loading = isLoading || isFetching;

  // NEW: frontend search behavior aligned with Company Management smart search.
  // - case-insensitive
  // - partial match from anywhere in key fields
  // - empty search shows all products
  const normalizedSearch = useMemo(
    () => (debouncedSearchTerm ? debouncedSearchTerm.trim().toLowerCase() : ""),
    [debouncedSearchTerm]
  );

  const filteredProducts = useMemo(() => {
    if (!normalizedSearch) return products;
    return (products || []).filter((p) => {
      const sku = String(p.sku ?? "").toLowerCase();
      const name = String(p.skuName ?? "").toLowerCase();
      // const desc = String(p.description ?? "").toLowerCase();
      const companyText = (p.companies || [])
        .map((c) =>
          `${c.name || ""} ${c.companyCode || ""}`
            .toString()
            .toLowerCase()
        )
        .join(" ");

      return (
        sku.includes(normalizedSearch) ||
        name.includes(normalizedSearch) ||
        // desc.includes(normalizedSearch) ||
        companyText.includes(normalizedSearch)
      );
    });
  }, [products, normalizedSearch]);

  // Date / Shelf Life: any 2 of 3 → auto-calc 3rd and lock it.
  // Recalculation runs on every relevant change so values stay in sync.
  useEffect(() => {
    const mfg = normalizeDateInput(formData.manufacturingDate);
    const exp = normalizeDateInput(formData.expiryDate);
    const shelf = parseShelfDays(formData.shelfLifeDays);
    const hasMfg = !!mfg;
    const hasExp = !!exp;
    const hasShelf = shelf !== null;
    const count = (hasMfg ? 1 : 0) + (hasExp ? 1 : 0) + (hasShelf ? 1 : 0);

    if (count < 2) {
      setDerivedDateField(null);
      setFormErrors((prev) => ({ ...prev, shelfLifeDays: undefined }));
      return;
    }

    // Priority rules (re-evaluated on every change):
    // 1) If manufacturingDate + expiryDate present → always recalc shelfLifeDays
    if (hasMfg && hasExp) {
      const newShelf = diffDays(mfg, exp);
      const newShelfStr = String(newShelf);
      setFormData((prev) =>
        prev.shelfLifeDays === newShelfStr ? prev : { ...prev, shelfLifeDays: newShelfStr }
      );
      setDerivedDateField("shelfLifeDays");
      setFormErrors((prev) => ({ ...prev, shelfLifeDays: undefined }));
      return;
    }

    // 2) If manufacturingDate + shelfLifeDays present → always recalc expiryDate
    if (hasMfg && hasShelf) {
      const newExp = formatDateYMD(addDaysToDate(mfg, shelf));
      setFormData((prev) =>
        prev.expiryDate === newExp ? prev : { ...prev, expiryDate: newExp }
      );
      setDerivedDateField("expiryDate");
      setFormErrors((prev) => ({ ...prev, shelfLifeDays: undefined }));
      return;
    }

    // 3) If expiryDate + shelfLifeDays present → always recalc manufacturingDate
    if (hasExp && hasShelf) {
      const newMfg = formatDateYMD(subtractDaysFromDate(exp, shelf));
      setFormData((prev) =>
        prev.manufacturingDate === newMfg ? prev : { ...prev, manufacturingDate: newMfg }
      );
      setDerivedDateField("manufacturingDate");
      setFormErrors((prev) => ({ ...prev, shelfLifeDays: undefined }));
      return;
    }

    // Fallback: clear error / derived field when logic doesn't apply
    setFormErrors((prev) => ({ ...prev, shelfLifeDays: undefined }));
    setDerivedDateField(null);
  }, [formData.manufacturingDate, formData.expiryDate, formData.shelfLifeDays]);

  const resetForm = () => {
    setFormData({
      sku: "",
      skuName: "",
      // description: "",
      storageType: "",
      productType: "",
      uom: "",
      basePrice: 0,
      casesPerPallet: "",
      innerUom: "",
      palletUom: "",
      innerUomQty: "",
      // outerUomQty removed from Product configuration
      // outerUomQty: "",
      conversionQtyPerPallet: "",
      qtyInPallet: "",
      weight: 0,
      weightPerPallet: 0,
      dimensions: { length: 0, breadth: 0, height: 0 },
      isBatchControlled: true,
      companies: [],

      supplierName: "",
      skuCategory: "NON_CORE",
      // manufacturingDate, expiryDate, location removed from Product Master
      // manufacturingDate: "",
      // expiryDate: "",
      shelfLifeDays: "",
      // location: "",
    });
    setEditingProductId(null);
    setSavedQtyInPallet(undefined);
    setFormErrors({});
    setTouched({});
    setDerivedDateField(null);
  };

  const handleSubmit = async (e) => {
   if (e && e.preventDefault) e.preventDefault();

   if (formErrors.shelfLifeDays) {
     toast.error(formErrors.shelfLifeDays);
     return;
   }
   const dataToSubmit = e && e.preventDefault ? formData : e;
   const { length, breadth, height } = dataToSubmit.dimensions || {};

   if (!length || length <= 0) {
     toast.error("Length is required and must be greater than 0");
     return;
   }
   
   if (!breadth || breadth <= 0) {
     toast.error("Breadth is required and must be greater than 0");
     return;
   }
   
   if (!height || height <= 0) {
     toast.error("Height is required and must be greater than 0");
     return;
   }
   // ✅ 1) basePrice normalize + validate (ADD HERE)
   const basePriceNum =
     dataToSubmit?.basePrice === "" || dataToSubmit?.basePrice == null
       ? 0
       : Number(dataToSubmit.basePrice);
 
   if (Number.isNaN(basePriceNum) || basePriceNum < 0) {
     toast.error("Base Price must be a non-negative number");
     return;
   }
 
   // ✅ 2) inner config optional mapping + validate (ADD HERE)
   const outerUom = String(dataToSubmit?.uom || "").trim().toUpperCase();
   if (!outerUom) {
     toast.error("Please select  UOM");
     return;
   }

   if (!String(dataToSubmit?.storageType || "").trim()) {
     toast.error("Storage Type is required");
     return;
   }
   if (!String(dataToSubmit?.productType || "").trim()) {
     toast.error("Product Type is required");
     return;
   }

   const innerUom = String(dataToSubmit?.innerUom || "").trim().toUpperCase();

   // If Outer UOM is PCS / KG / L (lowest unit), Inner UOM is not allowed
   if (["PCS", "KG", "L"].includes(outerUom) && innerUom) {
     toast.error("Inner UOM is not allowed when Outer UOM is PCS, KG, or L");
     return;
   }
   
   let innerUomConfig = undefined;
   
   if (innerUom) {
     if (innerUom === outerUom) {
       toast.error("Inner UOM cannot be same as Outer UOM");
       return;
     }
     innerUomConfig = { uom: innerUom }; 
   }

   const parseOptionalNonNegativeNumber = (value, label) => {
     if (value === undefined || value === null) return undefined;
     const s = String(value).trim();
     if (!s) return undefined;
     const n = Number(s);
     if (Number.isNaN(n) || n < 0) {
       toast.error(`${label} must be a non-negative number`);
       return "__INVALID__";
     }
     return n;
   };

   const normalizedPalletUom = String(dataToSubmit?.palletUom || "").trim().toUpperCase();
   const palletUomValue = normalizedPalletUom ? normalizedPalletUom : undefined;

   // outerUomQty removed from Product configuration - do not use in active flow
   // const outerUomQtyNum = parseOptionalNonNegativeNumber(dataToSubmit?.outerUomQty, "Outer UOM Qty");
   const innerUomQtyNum = parseOptionalNonNegativeNumber(dataToSubmit?.innerUomQty, "Inner UOM Qty");
  if (innerUomQtyNum === "__INVALID__") {
    toast.error("Inner UOM Qty must be 0 or greater");
    return;
  }
   if (innerUom && (innerUomQtyNum === undefined || innerUomQtyNum === null || innerUomQtyNum <= 0)) {
     toast.error("Inner UOM Qty is required when Inner UOM is selected");
     return;
   }
   const conversionQtyPerPalletNum = parseOptionalNonNegativeNumber(
     dataToSubmit?.conversionQtyPerPallet,
     "Conversion Qty Per Pallet"
   );
   if (
    dataToSubmit?.conversionQtyPerPallet === "" ||
    dataToSubmit?.conversionQtyPerPallet == null
  ) {
    toast.error("Conversion Qty per Pallet must be 1 or greater");
    return;
  }
  if (!conversionQtyPerPalletNum || conversionQtyPerPalletNum < 1) {
   toast.error("Conversion Qty per Pallet must be 1 or greater");
   return;
 }
   if (conversionQtyPerPalletNum === "__INVALID__") {
    toast.error("Conversion Qty per Pallet must be 1 or greater");
    return;
  }

   if (!innerUom && innerUomQtyNum !== undefined) {
     toast.error("Inner UOM must be selected when Inner UOM Qty is entered");
     return;
   }
   // Outer UOM Qty no longer required when Inner UOM selected - validation removed
   // if (innerUom && outerUomQtyNum === undefined) {
   //   toast.error("Outer UOM Qty is required when Inner UOM is selected");
   //   return;
   // }
   // ✅ 3) NOW build payload (manufacturingDate, expiryDate, location, outerUomQty removed from Product Master)
   const payload = {
     ...dataToSubmit,
     sku: dataToSubmit?.sku?.trim() ? dataToSubmit.sku.trim() : undefined,
     skuName: dataToSubmit?.skuName?.trim(),
     // description: dataToSubmit?.description?.trim(),
     storageType: dataToSubmit.storageType,
     productType: dataToSubmit.productType,

     supplierName: dataToSubmit?.supplierName?.trim(),
     skuCategory: dataToSubmit?.skuCategory,

     // manufacturingDate, expiryDate, location removed from Product Master - do not send
     // manufacturingDate: dataToSubmit?.manufacturingDate ? dataToSubmit.manufacturingDate : undefined,
     // expiryDate: dataToSubmit?.expiryDate ? dataToSubmit.expiryDate : undefined,
     shelfLifeDays:
       dataToSubmit?.shelfLifeDays !== "" && dataToSubmit?.shelfLifeDays != null
         ? Number(dataToSubmit.shelfLifeDays)
         : undefined,
     // location: dataToSubmit?.location?.trim() ? dataToSubmit.location.trim() : undefined,

     basePrice: basePriceNum,
     innerUomConfig,
     // Single source of truth for weight in backend
     grossWeightAsPerPalletUom: dataToSubmit?.weight,
   };
   delete payload.innerUom;
   delete payload.innerQtyPerOuter;
   delete payload.palletUom;
   delete payload.innerUomQty;
   // delete payload.outerUomQty;
   delete payload.conversionQtyPerPallet;
   delete payload.weightPerPallet;

   if (palletUomValue) payload.palletUom = palletUomValue;
   // if (outerUomQtyNum !== undefined) payload.outerUomQty = outerUomQtyNum;
   if (innerUomQtyNum !== undefined) payload.innerUomQty = innerUomQtyNum;
   if (conversionQtyPerPalletNum !== undefined) payload.conversionQtyPerPallet = conversionQtyPerPalletNum;

   // Edit only: clearing Inner UOM must persist — JSON omits undefined, so send explicit nulls for the API
   if (!innerUom && editingProductId) {
     payload.innerUomConfig = null;
     payload.innerUomQty = null;
   }

  // casesPerPallet: include only if value is present; otherwise omit
  const cppRaw = dataToSubmit?.casesPerPallet;
  if (cppRaw !== "" && cppRaw != null && cppRaw !== 0) {
    payload.casesPerPallet = Number(cppRaw);
  }

  try {
    if (editingProductId) {
       console.log("Submitting product update", dataToSubmit);
       console.log("UPDATE PRODUCT PAYLOAD", payload);
       const res = await productService.updateProduct(editingProductId, payload);
       const updatedProduct = res?.data || res;
       if (updatedProduct && (updatedProduct._id || updatedProduct.id)) {
         setProducts((prev) =>
           (prev || []).map((p) => {
             const pid = p?._id || p?.id;
             const upid = updatedProduct._id || updatedProduct.id;
             return String(pid) === String(upid) ? { ...p, ...updatedProduct } : p;
           })
         );
       }
       // console.log("API Response Product:", res?.data);
       // console.log("Qty In Pallet from backend:", res?.data?.qtyInPallet);
       setSavedQtyInPallet(res?.data?.qtyInPallet ?? res?.qtyInPallet);
       setFormData((prev) => ({
         ...prev,
         qtyInPallet: res?.data?.qtyInPallet ?? res?.qtyInPallet ?? "",
       }));
       toast.success("Product saved successfully");
       resetForm();
       setShowForm(false);
       await refetchProducts();
     } else {
       const res = await productService.createProduct(payload);
       if (res?.error) {
         const err = new Error(res.message || res.error || "Failed to create product");
         err.response = { data: res };
         throw err;
       }
       // console.log("API Response Product:", res?.data);
       // console.log("Qty In Pallet from backend:", res?.data?.qtyInPallet);
       setSavedQtyInPallet(res?.data?.qtyInPallet ?? res?.qtyInPallet);
       setFormData((prev) => ({
         ...prev,
         qtyInPallet: res?.data?.qtyInPallet ?? res?.qtyInPallet ?? "",
       }));
       toast.success("Product saved successfully");
       resetForm();
       setShowForm(false);
       await refetchProducts();
     }
   } catch (error) {
     console.error("Product Master error:", error);
     let msg = getApiErrorMessage(error);
     toast.error(msg);
   }
 };
  const productFields = [
    // ROW 1
    {
      name: "sku",
      label: "Customer SKU Code",
      type: "text",
      placeholder: "Auto-create if blank",
      required: false,
      grid: "1",
      disabled: !!editingProductId,
    },
    { name: "skuName", label: "SKU Name", type: "text", required: true, grid: "1" },
    // {
    //   // name: "description",
    //   // label: "Product Description",
    //   // type: "text",
    //   // required: true,
    //   // grid: "1",
    //   type: "custom",
    //   grid: "1",
    //   render: () => null,
    // },
    {
      name: "storageType",
      label: "Storage Type",
      type: "select",
      required: true,
      placeholder: "Select Storage Type",
      options: [
        { label: "Frozen", value: "FROZEN" },
        { label: "Chilled", value: "CHILLED" },
        { label: "Tropical", value: "TROPICAL" },
        { label: "Dried", value: "DRIED" },
      ],
      grid: "1",
    },
    {
      name: "productType",
      label: "Product Type",
      type: "select",
      required: true,
      placeholder: "Select Product Type",
      options: [
        { label: "Food", value: "FOOD" },
        { label: "Non Food", value: "NON FOOD" },
        { label: "Chemical", value: "CHEMICAL" },
      ],
      grid: "1",
    },
    {
      name: "companies",
      label: "Company",
      type: "multiselect",
      placeholder: "-- Select Company --",
      options: filteredCompanies.map((c) => ({
        label: `${c.companyCode} - ${c.name}`,
        value: c._id || c.id,
      })),
      grid: "1",
    },
    {
      name: "basePrice",
      label: "Base Price",
      type: "number",
      required: false,
      min: 0,
      step: 0.01,
      grid: "1",
    },
    // ROW 2 (5 fields) - Outer UOM first option = placeholder; Inner UOM visible only after Outer UOM selected; PCS hides Inner UOM
    {
      name: "uom",
      label: "UOM",
      type: "select",
      required: true,
      placeholder: "Select Outer UOM",
      options: [
        // { label: "Select Outer UOM", value: "" },
        { label: "Pieces (PCS)", value: "PCS" },
        { label: "Kilogram (KG)", value: "KG" },
        { label: "Liter (L)", value: "L" },
        { label: "Pack", value: "PACK" },
        { label: "Case", value: "CASE" },
        { label: "Drum", value: "DRUM" },
        { label: "Bag", value: "BAG" },
      ],
      grid: "1",
    },
    // Outer UOM Qty removed from Product configuration - do not use in active flow
    // {
    //   type: "custom",
    //   grid: "1",
    //   render: () => (
    //     <div className="col-span-1">
    //       <label className="form-popup-label mb-1.5 block">Outer UOM Qty</label>
    //       <input
    //         type="number"
    //         min={0}
    //         step={0.01}
    //         value={formData.outerUomQty}
    //         onChange={(e) => setFormData((prev) => ({ ...prev, outerUomQty: e.target.value }))}
    //         placeholder="Optional"
    //         className="w-full"
    //       />
    //     </div>
    //   ),
    // },
    // {
    //   type: "custom",
    //   grid: "1",
    //   render: () => {
    //     const outerUomSelected = String(formData.uom || "").trim();
    //     const isRestrictedOuter = ["PCS", "L", "KG"].includes(outerUomSelected);
    //     const showInnerUom = !!outerUomSelected && !isRestrictedOuter;
    //     return (
    //       <div className="col-span-1">
    //         <label className="form-popup-label mb-1.5 block">Inner UOM (Optional)</label>
    //         <select
    //           value={formData.innerUom || ""}
    //           onChange={(e) => {
    //             const v = e.target.value;
    //             setFormData((prev) => ({
    //               ...prev,
    //               innerUom: v,
    //               ...(v === "" ? { innerUomQty: "" } : {}),
    //             }));
    //           }}
    //           disabled={!showInnerUom}
    //           className={`w-full ${!showInnerUom ? "cursor-not-allowed opacity-60" : ""}`}
    //         >
    //           <option value="">Select Inner UOM</option>
    //           {["PCS", "KG", "L", "PACK", "CASE", "DRUM", "BAG"].map((opt) => (
    //             <option key={opt} value={opt} disabled={opt === outerUomSelected}>
    //               {opt === "PCS" ? "Pieces (PCS)" : opt === "L" ? "Liter (L)" : opt}
    //             </option>
    //           ))}
    //         </select>
    //         {/* {isRestrictedOuter && (
    //           <small className="text-gray-500">Inner UOM not allowed when Outer UOM is PCS, L, or KG</small>
    //         )} */}
    //       </div>
    //     );
    //   },
    // },
    // {
    //   type: "custom",
    //   grid: "1",
    //   render: () => {
    //     const innerUomSelected = String(formData.innerUom || "").trim();
    //     const outerIsRestricted = ["PCS", "L", "KG"].includes(String(formData.uom || "").trim());
    //     const disabled = !innerUomSelected || outerIsRestricted;
    //     return (
    //       <div className="col-span-1">
    //         <label className="form-popup-label mb-1.5 block">Inner UOM Qty</label>
    //         <input
    //           type="number"
    //           name="innerUomQty"
    //           min={0}
    //           step={0.01}
    //           value={formData.innerUomQty}
    //           onChange={(e) => {
    //             const innerUomQty = e.target.value;
    //             // console.log("Conversion Qty Per Pallet:", formData.conversionQtyPerPallet);
    //             // console.log("Inner UOM Qty:", innerUomQty);
    //             // console.log("Current Form State:", formData);
    //             setFormData((prev) => ({ ...prev, innerUomQty }));
    //           }}
    //           placeholder={disabled ? "Select Inner UOM first" : "Optional"}
    //           disabled={disabled}
    //           className={`w-full ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    //         />
    //       </div>
    //     );
    //   },
    // },
    // {
    //   type: "custom",
    //   grid: "1",
    //   render: () => {
    //     const outer = String(formData.uom || "").trim().toUpperCase();
    //     const inner = String(formData.innerUom || "").trim().toUpperCase();
    //     const restricted = ["PCS", "L", "KG"].includes(outer);
    //     const autoVal = inner && !restricted ? inner : outer;
    //     return (
    //       <div className="col-span-1">
    //         <label className="form-popup-label mb-1.5 block">Pallet UOM</label>
    //         <select
    //           value={formData.palletUom || autoVal || ""}
    //           disabled
    //           className="w-full cursor-not-allowed opacity-80 bg-[#F6F7FA]"
    //           aria-readonly
    //         >
    //           <option value="">—</option>
    //           {["PCS", "KG", "L", "PACK", "CASE", "DRUM", "BAG"].map((opt) => (
    //             <option key={opt} value={opt}>
    //               {opt === "PCS" ? "Pieces (PCS)" : opt === "L" ? "Liter (L)" : opt}
    //             </option>
    //           ))}
    //         </select>
    //       </div>
    //     );
    //   },
    // },
    {
      type: "custom",
      grid: "1",
      render: () => (
        // <div className="col-span-1">
        //   <label className="form-popup-label mb-1.5 block">Conversion Qty Per Pallet</label>
        //   <input
        //     type="number"
        //     min={0}
        //     step={0.01}
        //     value={formData.conversionQtyPerPallet}
        //     onChange={(e) => setFormData((prev) => ({ ...prev, conversionQtyPerPallet: e.target.value }))}
        //     placeholder="Required"
        //     className="w-full"
        //   />
        // </div>
        <div className="col-span-1">
  <label className="form-popup-label mb-1.5 block">
    Conversion Qty Per Pallet <span style={{ color: "red" }}>*</span>
  </label>

  <input
    type="text"
    name="conversionQtyPerPallet"
    value={
      formData.conversionQtyPerPallet
        ? Number(formData.conversionQtyPerPallet).toLocaleString("en-IN")
        : ""
    }
    onChange={(e) => {
      let value = e.target.value;

      // Remove commas
      value = value.replace(/,/g, "");

      // Allow only numbers (no e, no symbols)
      if (!/^\d*$/.test(value)) return;

      const conversionQtyPerPallet = value === "" ? "" : Number(value);
      // console.log("Conversion Qty Per Pallet:", conversionQtyPerPallet);
      // console.log("Inner UOM Qty:", formData.innerUomQty);
      // console.log("Current Form State:", formData);

      setFormData((prev) => ({
        ...prev,
        conversionQtyPerPallet,
      }));
    }}
    placeholder="Required"
    className="w-full"
  />
</div>
      ),
    },
    {
     name: "weight",
     label: "Gross Weight (Pallet UOM)",
     type: "number",
     required: true,
     min: 1,
     step: 0.01,
     grid: "1",
   },
    {
      type: "custom",
      grid: "1",
      render: () => (
        <div className="col-span-1">
          <label className="form-popup-label mb-1.5 block">Weight Per Pallet (kg)</label>
          <input
            type="text"
            name="weightPerPalletDisplay"
            value={
              uiWeightPerPallet
                ? Number(uiWeightPerPallet).toLocaleString("en-IN", {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  })
                : "0"
            }
            readOnly
            disabled
            tabIndex={-1}
            style={{ background: "#F6F7FA" }}
            className="w-full"
          />
        </div>
      ),
    },
    {
      type: "custom",
      grid: "1",
      render: () => {
        const stateQty = Number(formData.qtyInPallet);
        const fallback = Number.isFinite(savedQtyInPallet) ? savedQtyInPallet : undefined;
        const valueToShow = Number.isFinite(stateQty) ? stateQty : fallback;
        const display =
          valueToShow === undefined
            ? "—"
            : String(Number(valueToShow.toFixed(2)));

        return (
          // <div className="col-span-1">
          //   <label className="form-popup-label mb-1.5 block">Qty In Pallet</label>
          //   <input
          //     type="text"
          //     value={display}
          //     readOnly
          //     aria-readonly="true"
          //     tabIndex={-1}
          //     style={{ background: "#F6F7FA" }}
          //     className="w-full"
          //   />
          // </div>
          <div className="col-span-1">
  <label className="form-popup-label mb-1.5 block">Qty In Pallet</label>
  {/* <div style={{ color: "red" }}>
    DEBUG Qty In Pallet: {formData.qtyInPallet}
  </div> */}
  <input
    type="text"
    name="qtyInPallet"
    value={
      display
        ? Number(display).toLocaleString("en-IN")
        : ""
    }
    readOnly
    aria-readonly="true"
    tabIndex={-1}
    style={{ background: "#F6F7FA" }}
    className="w-full"
  />
</div>
        );
      },
    },
    { name: "supplierName", label: "Supplier Name", type: "text", required: false, grid: "1" },
    {
      name: "skuCategory",
      label: "SKU Category",
      type: "select",
      required: false,
      options: [
        { label: "Core", value: "CORE" },
        { label: "Non Core", value: "NON_CORE" },
      ],
      grid: "1",
    },
    // ROW 3 - manufacturingDate, expiryDate, location removed from Product Master (do not use in active flow)
    // {
    //   name: "manufacturingDate",
    //   label: "Manufacturing Date",
    //   type: "date",
    //   required: false,
    //   grid: "1",
    //   disabled: derivedDateField === "manufacturingDate",
    // },
    // {
    //   name: "expiryDate",
    //   label: "Expiry Date",
    //   type: "date",
    //   required: false,
    //   grid: "1",
    //   disabled: derivedDateField === "expiryDate",
    // },
    {
      name: "shelfLifeDays",
      label: "Shelf Life (Days)",
      type: "number",
      required: true,
      placeholder: "Enter shelf life",
      min: 0,
      grid: "1",
      disabled: derivedDateField === "shelfLifeDays",
    },
    // { name: "location", label: "Location", type: "text", required: false, grid: "1" },
    {
      name: "casesPerPallet",
      label: "Pallet(Optional)",
      type: "number",
      required: false,
      min: 1,
      placeholder: "e.g., 40",
      grid: "1",
    },
    // Dimensions section card (full width)
    {
      type: "custom",
      grid: "full",
      render: () => {
       const hasError = (val) => !val || val <= 0;
       return (
        <div
        style={{
          border: "1px solid #E4E7EC",
          borderRadius: 14,
          backgroundColor: "#fff",
          padding: 18,
          marginTop: 16,
        }}
      >
        <h3
          style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#101828",
            marginBottom: 16,
          }}
        >
          Dimensions (cm)
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 26,
          }}
        >
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "#344054" }}>
              Length <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="number"
              min={0}
              step={0.1}
              value={formData.dimensions?.length ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  dimensions: {
                    ...prev.dimensions,
                    length: parseFloat(e.target.value) || 0,
                  },
                }))
              }
              className={`w-full rounded-[10px] border ${
               hasError(formData.dimensions?.length)
                 ? "border-red-500"
                 : "border-[#D0D5DD]"
             } bg-white text-sm placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]`}
             style={{ height: 44, padding: "0 14px" }}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "#344054" }}>
              Breadth <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="number"
              min={0}
              step={0.1}
              value={formData.dimensions?.breadth ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  dimensions: {
                    ...prev.dimensions,
                    breadth: parseFloat(e.target.value) || 0,
                  },
                }))
              }
              className={`w-full rounded-[10px] border ${
               hasError(formData.dimensions?.breadth)
                 ? "border-red-500"
                 : "border-[#D0D5DD]"
             } bg-white text-sm placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2" style={{ color: "#344054" }}>
              Height <span style={{ color: "red" }}>*</span>
            </label>
            <input
              type="number"
              min={0}
              step={0.1}
              value={formData.dimensions?.height ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  dimensions: {
                    ...prev.dimensions,
                    height: parseFloat(e.target.value) || 0,
                  },
                }))
              }
              className={`w-full rounded-[10px] border ${
               hasError(formData.dimensions?.height)
                 ? "border-red-500"
                 : "border-[#D0D5DD]"
             } bg-white text-sm placeholder-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]`}
            />
          </div>
        </div>
      </div>
       )
      },
    },
    // Batch Controlled Product (full width, left aligned)
    { name: "isBatchControlled", label: "Batch Controlled Product", type: "yesno", grid: "full" },
  ];

  const handleEdit = (row) => {
    const originalCompanyIds = (row.companies || []).map((c) => c._id || c.id);
    const approvedIds = new Set((filteredCompanies || []).map((c) => String(c._id || c.id)));
    let nextCompanyIds = originalCompanyIds;
    let mismatch = false;
    if ((companies || []).length && originalCompanyIds.length) {
      if (!originalCompanyIds.every((id) => approvedIds.has(String(id)))) {
        nextCompanyIds = [];
        mismatch = true;
      }
    }
    const outerU = String(row.uom || "").trim().toUpperCase();
    const innerFromRow = String(row.innerUomConfig?.uom || "").trim().toUpperCase();
    const restrictedOuter = ["PCS", "L", "KG"].includes(outerU);
    const palletUomForEdit = restrictedOuter
      ? outerU || row.palletUom || ""
      : (innerFromRow || outerU) || row.palletUom || "";

    setFormData({
      sku: row.sku || "",
      skuName: row.skuName || "",
      // description: row.description || "",
      storageType: row.storageType || "",
      productType: row.productType || "",
      uom: row.uom || "",
      weight: row.weight || 0,
      weightPerPallet: row.weightPerPallet != null && row.weightPerPallet !== "" ? Number(row.weightPerPallet) : 0,
      dimensions: {
        length: row.dimensions?.length || 0,
        breadth: row.dimensions?.breadth || 0,
        height: row.dimensions?.height || 0,
      },
      isBatchControlled: typeof row.isBatchControlled === "boolean" ? row.isBatchControlled : false,
      companies: nextCompanyIds,
      basePrice: typeof row.basePrice === "number" ? row.basePrice : 0,
      casesPerPallet: row.casesPerPallet != null && row.casesPerPallet !== "" ? row.casesPerPallet : "",

      innerUom: row.innerUomConfig?.uom || "",
      palletUom: palletUomForEdit,
      // outerUomQty removed from Product configuration - do not load in edit
      innerUomQty: row.innerUomQty != null && row.innerUomQty !== "" ? String(row.innerUomQty) : "",
      conversionQtyPerPallet:
        row.conversionQtyPerPallet != null && row.conversionQtyPerPallet !== ""
          ? String(row.conversionQtyPerPallet)
          : "",
      qtyInPallet:
        row.qtyInPallet != null && row.qtyInPallet !== ""
          ? String(row.qtyInPallet)
          : "",
      supplierName: row.supplierName || "",
      skuCategory: row.skuCategory || "NON_CORE",
      // manufacturingDate, expiryDate, location removed from Product Master - do not load in edit
      shelfLifeDays:
        row.shelfLifeDays != null && row.shelfLifeDays !== ""
          ? String(row.shelfLifeDays)
          : "",
    });

    setEditingProductId(row._id || row.id);
    setSavedQtyInPallet(row.qtyInPallet);
    setShowForm(true);
    if (mismatch) {
      toast.error("Selected company is not approved. Please select an approved company.");
    }
  };

  const handleDelete = (row) => {
    // Product delete disabled in UI — modal open commented out (re-enable with Delete action + DeletePopUp)
    // setRowToDelete(row);
    // setDeletePopUpOpen(true);
  };

  const openView = async (row) => {
    viewRowRef.current = row;
    setViewOpen(true);
    setViewLoading(true);
    setViewError(null);
    setViewData(null);
    try {
      const id = row?._id || row?.id;
      const res = await productService.getProductById(id);
      if (res?.success === false) {
        setViewError(res?.error || res?.message || "Failed to load product details");
        return;
      }
      const product = res?.data || res;
      setViewData(product);
    } catch (err) {
      console.error("Error fetching product details:", err);
      setViewError(err?.message || "Failed to load product details");
      setViewData(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!rowToDelete) return;
    const id = rowToDelete._id || rowToDelete.id;
    try {
      setDeleteLoading(true);
      const result = await productService.deleteProduct(id);
      setDeletePopUpOpen(false);
      setRowToDelete(null);
      await refetchProducts();
      const message = result?.message || (result?.modified ? "Product deactivated successfully" : "Product deactivated");
      toast.success(message);
    } catch (err) {
      console.error("Product Master error:", err);
      toast.error(getApiErrorMessage(err));
    } finally {
      setDeleteLoading(false);
    }
  };

  const productActions = [
    { label: "Edit", icon: EditIcon, onClick: handleEdit },
    // Product delete disabled in UI — re-enable to show Delete in row actions
    // { label: "Delete", icon: DeleteIcon, onClick: handleDelete },
  ];

  const resetUploadPreview = () => {
    setUploadPreviewOpen(false);
    setUploadPreviewFileName("");
    setUploadPreviewHeaders([]);
    setUploadPreviewRows([]);
    setUploadInvalidPreviewRows([]);
    setUploadSummary({ totalRows: 0, validRows: 0, invalidRows: 0 });
    setUploadHasCriticalErrors(false);
    setUploadSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUploadProductClick = () => fileInputRef.current?.click();

  const handleUploadFileSelect = async (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    try {
      const { headers, rows, allRows, totalRows, fileName } = await parseFileToPreview(file);
      const sourceRows = Array.isArray(allRows) ? allRows : rows;
      const filteredRows = (sourceRows || []).filter((row) => !isRowEmpty(row));
      const validation = validatePreviewRows(filteredRows);
      const previewRows = filteredRows.slice(0, 5);
      const rowsWithQty = (previewRows || []).map((row) => {
        const conversion = Number(
          getColumnValue(row, [
            "Conversion Qty Per Pallet",
            "conversionQtyPerPallet",
            "Conversion Quantity Per Pallet",
            "Conversion Per Pallet",
          ]) || 0
        );
        const innerQty = Number(
          getColumnValue(row, [
            "Inner UOM Qty",
            "innerUomQty",
            "Inner Uom Qty",
            "Inner UOM Quantity",
          ]) || 0
        );
        const innerUomCell = getColumnValue(row, ["innerUom", "Inner UOM", "Inner Uom"]);
        const hasInnerUom =
          innerUomCell !== undefined && innerUomCell !== null && String(innerUomCell).trim() !== "";

        let qtyInPallet = "";
        if (conversion >= 1) {
          if (hasInnerUom) {
            if (Number.isFinite(innerQty) && innerQty > 0) {
              qtyInPallet = 1;
            }
          } else {
            qtyInPallet = 1;
          }
        }

        console.log("QtyInPallet Calculation:", {
          innerUom: hasInnerUom ? String(innerUomCell).trim() : undefined,
          innerUomQty: hasInnerUom ? innerQty : undefined,
          conversionQtyPerPallet: conversion,
          result: qtyInPallet === "" ? undefined : qtyInPallet,
        });

        console.log("Row:", row);
        console.log("Conversion:", conversion);
        console.log("Inner Qty:", innerQty);
        console.log("Qty In Pallet:", qtyInPallet);

        const weightRaw = getColumnValue(row, [
          "weight",
          "Gross Weight as per Pallet UOM (kg)",
          "Gross Weight",
        ]);
        const grossW = Number(weightRaw);
        const weightPerPallet =
          Number.isFinite(grossW) &&
          Number.isFinite(conversion) &&
          conversion >= 1
            ? grossW * conversion
            : 0;

        return {
          ...row,
          qtyInPallet: Number.isFinite(qtyInPallet) ? qtyInPallet : "",
          "Weight Per Pallet": weightPerPallet,
        };
      });
      let previewHeadersWithQty = headers.includes("qtyInPallet") ? headers : [...headers, "qtyInPallet"];
      if (!previewHeadersWithQty.includes("Weight Per Pallet")) {
        previewHeadersWithQty = [...previewHeadersWithQty, "Weight Per Pallet"];
      }
      setUploadPreviewFileName(fileName);
      setUploadPreviewHeaders(previewHeadersWithQty);
      setUploadPreviewRows(rowsWithQty);
      setUploadInvalidPreviewRows(validation.invalidIdx.filter((i) => i < rows.length));
      setUploadSummary({
        totalRows: filteredRows.length,
        validRows: validation.summary.validRows,
        invalidRows: validation.summary.invalidRows,
      });
      setUploadHasCriticalErrors(validation.hasCriticalErrors);
      setUploadSelectedFile(file);
      setUploadPreviewOpen(true);
      if (validation.hasCriticalErrors && validation.rowErrors[0]) {
        toast.error(validation.rowErrors[0]);
      }
    } catch (err) {
      toast.error("Invalid Excel format detected");
    }
  };

  const handleDownloadFormat = () => {
    downloadProductUploadTemplate();
    toast.success("Format downloaded. Fill and upload to add products.");
  };

  const handleUploadApply = async () => {
    if (!uploadSelectedFile) {
      resetUploadPreview();
      return;
    }
    if (uploadHasCriticalErrors) {
      toast.error("Please fix validation errors before submitting");
      return false;
    }
    setUploadLoading(true);
    try {
      const res = await productService.bulkUploadProductExcel(uploadSelectedFile);
      // console.log("API Response Product:", res?.data);
      // console.log("Qty In Pallet from backend:", res?.data?.qtyInPallet);
      if (Array.isArray(res?.errors) && res.errors.length) {
        const msg = getBestErrorMessageFromData(res) || `Row ${res.errors[0]?.rowIndex ?? res.errors[0]?.row ?? "?"}: ${res.errors[0]?.error ?? res.errors[0]?.message ?? "Error"}`;
        toast.error(msg);
        setUploadLoading(false);
        return;
      }
      if (res?.success === false) {
        const msg = getBestErrorMessageFromData(res) || res.error || res.message || "Operation failed";
        toast.error(msg);
        setUploadLoading(false);
        return;
      }
      const created = res?.data?.created ?? 0;
      const errors = res?.data?.errors || [];
      const errCount = errors.length;

      if (created > 0 && errCount === 0) {
        toast.success("Bulk upload completed successfully", { autoClose: 4000 });
      } else if (created > 0 && errCount > 0) {
        toast.success(`✅ ${created} product(s) uploaded successfully.`, { autoClose: 3000 });
        setTimeout(() => {
          const errorMessages = errors.slice(0, 5).map((e) => e.error || (typeof e === "string" ? e : "Invalid data"));
          const errorText =
            errCount <= 5 ? errorMessages.join(". ") : `${errorMessages.join(". ")}. ...and ${errCount - 5} more error(s).`;
          toast.error(`⚠️ ${errCount} row(s) had errors: ${errorText}`, { autoClose: 6000 });
        }, 500);
      } else if (errCount > 0) {
        const errorMessages = errors.slice(0, 5).map((e) => e.error || (typeof e === "string" ? e : "Invalid data"));
        const errorText =
          errCount <= 5 ? errorMessages.join(". ") : `${errorMessages.join(". ")}. ...and ${errCount - 5} more error(s).`;
        toast.error(`❌ Upload failed. ${errCount} error(s) found: ${errorText}`, { autoClose: 7000 });
      } else {
        toast.info("ℹ️ No products found in file. Please check your file and try again.", { autoClose: 4000 });
      }

      await refetchProducts();
      setTimeout(() => resetUploadPreview(), 500);
      return true;
    } catch (err) {
      console.error("Product Master error:", err);
      const onboardedCompanyMessage = getOnboardedCompanyError(err);
      if (onboardedCompanyMessage) {
        toast.error(onboardedCompanyMessage, { autoClose: 5000 });
      } else {
        toast.error(getUploadErrorMsg(err), { autoClose: 5000 });
      }
      setTimeout(() => resetUploadPreview(), 500);
      return false;
    } finally {
      setUploadLoading(false);
    }
  };

  return (
    <div className="page-container font-nunito">
      <div className="mb-6 w-full">
        <div
          className="flex items-center justify-between w-full flex-wrap gap-4"
          style={{
            backgroundColor: "#FFFFFF",
            border: "1px solid #EEF1F5",
            borderRadius: 28,
            padding: "16px 24px",
            boxShadow: "0px 0px 4px 0px #959DA526",
          }}
        >
          <h1
            style={{
              fontFamily: "Nunito",
              fontSize: 18,
              fontWeight: 600,
              color: "#161616",
              margin: 0,
            }}
          >
            Product Master
          </h1>

          <div className="flex items-center gap-[14px] flex-wrap">
            <div className="relative" style={{ width: 320 }}>
              <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="h-4 w-4">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.3-4.3m1.8-5.2a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by SKU"
                className="w-full rounded-[12px] border border-[#D0D5DD] bg-white pl-10 pr-4 text-sm text-gray-800 placeholder:text-[#98A2B3] focus:outline-none focus:border-[#188742] focus:ring-2 focus:ring-[rgba(24,135,66,0.12)]"
                style={{ height: 44 }}
              />
            </div>

            <button
              type="button"
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
              className="shrink-0 rounded-[12px] bg-[#188742] text-white font-semibold active:scale-[0.98]"
              style={{ height: 44, paddingLeft: 18, paddingRight: 18 }}
            >
              Create Product
            </button>

            <div className="relative shrink-0" ref={menuRef}>
              <button
                type="button"
                onClick={() => setMenuOpen((o) => !o)}
                className="flex items-center justify-center rounded-[12px] border-[1.5px] border-[#188742] bg-white text-[#188742] transition hover:bg-[rgba(24,135,66,0.06)]"
                style={{ width: 44, height: 44 }}
                aria-label="More options"
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24" className="w-5 h-5">
                  <circle cx="12" cy="6" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="18" r="1.5" />
                </svg>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 top-full z-50 mt-1 rounded-[12px] border border-[#EEF1F5] bg-white shadow-lg overflow-hidden"
                  style={{ width: 220, padding: 8 }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      handleDownloadFormat();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#16A34A0D]"
                    style={{ height: 40 }}
                  >
                    Download Format
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      handleUploadProductClick();
                      setMenuOpen(false);
                    }}
                    className="w-full text-left rounded-[10px] px-3 text-[#101828] font-medium transition hover:bg-[#16A34A0D]"
                    style={{ height: 40 }}
                  >
                    Upload Product
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <input type="file" ref={fileInputRef} accept=".xlsx,.xls,.csv" className="hidden" onChange={handleUploadFileSelect} />

      <UploadPreviewPopUp
        open={uploadPreviewOpen}
        onClose={resetUploadPreview}
        title="Upload Product - File overview"
        fileName={uploadPreviewFileName}
        previewHeaders={uploadPreviewHeaders}
        previewRows={uploadPreviewRows}
        invalidPreviewRows={uploadInvalidPreviewRows}
        summary={uploadSummary}
        disableApply={uploadHasCriticalErrors}
        onApply={handleUploadApply}
        onCancel={resetUploadPreview}
        loading={uploadLoading}
      />

      {/* Product delete disabled in UI — re-enable with DeleteIcon/DeletePopUp imports + productActions Delete + handleDelete body */}
      {/* <DeletePopUp
        open={deletePopUpOpen}
        onClose={() => {
          setDeletePopUpOpen(false);
          setRowToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        entityLabel="product"
      /> */}

      <ProductDetailsModal
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewError(null);
          setViewData(null);
        }}
        loading={viewLoading}
        error={viewError}
        data={viewData}
        onRetry={() => viewRowRef.current && openView(viewRowRef.current)}
      />

      <FormPopup
        open={showForm}
        title={editingProductId ? "Edit Product" : "Create New Product"}
        onClose={() => {
          setShowForm(false);
          resetForm();
        }}
        onSubmit={handleSubmit}
        submitText={editingProductId ? "Update" : "Save"}
        values={formData}
        setValues={setFormData}
        fields={productFields}
        errors={formErrors}
        touched={touched}
        width="xl"
      />

      {loading ? (
        <SkeletonTable cols={PRODUCT_COLUMNS.length + 1} rows={8} />
      ) : (
        <>
          <Table
            columns={PRODUCT_COLUMNS}
            data={filteredProducts}
            defaultRowsPerPage={5}
            rowsPerPageOptions={[
              5,
              10,
              15,
              20,
              Math.max(1, filteredProducts.length || 1),
            ]}
            loading={false}
            emptyMessage="No products found"
            actions={productActions}
            onRowClick={(row) => openView(row)}
          />
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page <= 1 || loading}
              className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={loading || page >= totalPages}
              className="rounded-[10px] border border-[#D0D5DD] bg-white px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}
